import { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { useLocation } from "react-router-dom";
import { FaTimes } from "react-icons/fa";
import Marquee from "react-fast-marquee";
import { toast, Toaster } from "sonner";
import { API_BASE_URL } from "../config/apiConfig";
import { Crown, Star } from "lucide-react";

const pricingPlans = [
    // {
    //     name: "Premium",
    //     price: { monthly: 4500, annual: 40000 },
    //     description: "Most popular choice for serious students",
    //     icon: <Star className="w-6 h-6" />,
    //     color: "from-blue-500 to-blue-600",
    //     popular: true,
    // },
    {
        name: "Premium Plus",
        price: { monthly: 7000, annual: 70000 },
        description: "Ultimate package with exclusive features",
        icon: <Crown className="w-6 h-6" />,
        color: "from-purple-500 to-purple-600",
        popular: false,
    },
];

const SubscriptionModal = ({ onClose }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [billingCycle, setBillingCycle] = useState("monthly");
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [showBanner, setShowBanner] = useState(false);
    const location = useLocation();
    useEffect(() => {
        const storedPlan = localStorage.getItem("selectedPlan");

        if (storedPlan === "premium") {
            setIsOpen(true);
        }
    }, []);


    // Handle plan selection
    const handlePlanSelect = (plan) => setSelectedPlan(plan);

    // Proceed to payment via Paystack
    const proceedToPayment = async () => {
        if (!selectedPlan) {
            toast.error("Please select a plan first");
            return;
        }

        try {
            const token = localStorage.getItem("token");
            if (!token) throw new Error("User not authenticated");
            const decoded = jwtDecode(token);
            const userId = decoded?.id;
            const userEmail = decoded?.email;

            const amount = selectedPlan.price[billingCycle];

            const res = await fetch(`${API_BASE_URL}/api/sub/paystack/${userId}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ email: userEmail, amount }),
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Failed to initialize payment");

            // Redirect user to Paystack payment page
            window.location.href = data.payment_url;
        } catch (err) {
            console.error(err);
            toast.error(err.message || "Failed to initialize payment");
        }
    };

    // Banner logic for users without active subscription
    useEffect(() => {
        const checkSubscription = async () => {
            const token = localStorage.getItem("token");
            if (!token) {
                setShowBanner(true);
                return;
            }

            try {
                const res = await fetch(`${API_BASE_URL}/api/sub/single`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const data = await res.json();
                const activeSub = data.subscriptions?.find((sub) => sub.status === "Active");
                setShowBanner(!activeSub);
            } catch (err) {
                console.error(err);
                setShowBanner(true);
            }

            // Exclude banner on certain paths
            const excludedPaths = [
                "/login", "/register", "/", "/update-academics",
                "/onboarding", "/forgot-password", "/otp",
                "/reset-password", "/reset-otp", "/signup",
                "/privacy-policy", "/terms-of-service"
            ];
            if (excludedPaths.includes(location.pathname)) setShowBanner(false);
        };

        checkSubscription();
    }, [location.pathname]);

    useEffect(() => {
        const handler = (e) => {
            if (e.detail) setIsOpen(false); // closes modal automatically
        };
        window.addEventListener("subscription-updated", handler);
        return () => window.removeEventListener("subscription-updated", handler);
    }, []);

    const handleClose = () => {
        setIsOpen(false);
        onClose?.();

        const storedPlan = localStorage.getItem("selectedPlan");
        if (storedPlan === "premium") {
            localStorage.removeItem("selectedPlan");
        }
    };


    return (
        <>
            <Toaster position="top-right" richColors />

            {/* Banner */}
            {showBanner && !isOpen && (
                <div
                    onClick={() => setIsOpen(true)}
                    className="fixed top-0 left-0 w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white p-3 text-center cursor-pointer z-50 shadow-lg"
                >
                    <Marquee speed={50} gradient={false}>
                        <span className="font-semibold text-lg">
                            🎓 Unlock Premium Features – Subscribe Now! &nbsp; • &nbsp;
                            🎓 Unlock Premium Features – Subscribe Now! &nbsp; • &nbsp;
                        </span>
                    </Marquee>
                </div>
            )}

            {/* Modal */}
            {isOpen && (
                <div className="fixed z-[99] inset-0 flex items-center justify-center bg-black bg-opacity-70 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl relative max-h-[95vh] overflow-auto">

                        {/* Header */}
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                                    <span className="text-white font-bold text-lg">T</span>
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-gray-800">Teechaa Premium</h2>
                                    <p className="text-xs text-gray-500">Secure Subscription</p>
                                </div>
                            </div>
                            <button
                                className="text-gray-400 hover:text-gray-700 transition-colors"
                                onClick={handleClose}
                            >
                                <FaTimes size={20} />
                            </button>
                        </div>

                        {/* Plan Selection */}
                        <div className="p-6">
                            <h3 className="text-xl font-bold text-gray-800 mb-2">Choose Your Plan</h3>
                            <p className="text-sm text-gray-600 mb-6">Select the plan that best fits your needs</p>

                            {/* Billing Cycle Toggle */}
                            <div className="flex justify-center gap-2 mb-6 bg-gray-100 p-1 rounded-xl">
                                {["monthly", "annual"].map((cycle) => (
                                    <button
                                        key={cycle}
                                        className={`flex-1 px-4 py-2 rounded-lg font-semibold text-sm transition-all ${billingCycle === cycle
                                            ? "bg-white text-blue-600 shadow-sm"
                                            : "text-gray-600 hover:text-gray-800"
                                            }`}
                                        onClick={() => setBillingCycle(cycle)}
                                    >
                                        {cycle.charAt(0).toUpperCase() + cycle.slice(1)}
                                        {cycle === "annual" && <span className="ml-1 text-xs text-green-600">Save 17%</span>}
                                    </button>
                                ))}
                            </div>

                            {/* Plan Cards */}
                            <div className="space-y-3 mb-6">
                                {pricingPlans.map((plan) => (
                                    <div
                                        key={plan.name}
                                        className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${selectedPlan?.name === plan.name
                                            ? "border-blue-500 bg-blue-50 shadow-md"
                                            : "border-gray-200 hover:border-blue-300 hover:shadow-sm"
                                            }`}
                                        onClick={() => handlePlanSelect(plan)}
                                    >
                                        {plan.popular && (
                                            <span className="absolute -top-2 right-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                                                Most Popular
                                            </span>
                                        )}
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className={`p-2 rounded-lg bg-gradient-to-r ${plan.color} text-white`}>
                                                {plan.icon}
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="font-bold text-lg text-gray-800">{plan.name}</h4>
                                                <p className="text-xs text-gray-600">{plan.description}</p>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-2xl font-bold text-blue-600">
                                                    ₦{plan.price[billingCycle].toLocaleString()}
                                                </div>
                                                <div className="text-xs text-gray-500">per {billingCycle}</div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <button
                                className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={proceedToPayment}
                                disabled={!selectedPlan}
                            >
                                Continue to Payment
                            </button>
                        </div>

                    </div>
                </div>
            )}
        </>
    );
};

export default SubscriptionModal;
